/**
 * Marketing copy shared between the home page and the detail pages, so a
 * summary and the page it links to cannot drift apart.
 *
 * Names used in examples are invented. No customer's data appears here.
 */

export const COURTS = [
  { unit: 'Court 1', sport: 'Badminton', home: 'Vihaan Patel', away: 'Diya Desai', score: '11 – 7', note: 'Game 2 · Games 1–0', live: true },
  { unit: 'Court 2', sport: 'Pickleball', home: 'A. Nair / P. Mehta', away: 'D. Kapoor / P. Iyer', score: '7 – 9', note: 'Game 1', live: true },
  { unit: 'Pitch 1', sport: 'Box Cricket', home: 'Ridgeway Reds', away: 'Harbour Blues', score: '64/3', note: '8.2 of 10 overs', live: true },
  { unit: 'Board 3', sport: 'Carrom', home: 'Isha Nair', away: 'Neha Rao', score: '21 – 14', note: 'Finished 11:40', live: false },
];

export const SPORTS = [
  {
    sport: 'Box cricket',
    how: 'Ball by ball — runs, extras and wickets, with an undo',
    detail:
      'Scored delivery by delivery. Wides and no-balls add their run and do not consume a ball. The innings closes on the over limit or when the side is all out, and the app refuses a wicket once the last batter is gone, so an innings cannot quietly run past its end.',
    units: 'Pitches',
    produces: 'Full scorecard, over by over, plus most runs, most wickets and most sixes.',
  },
  {
    sport: 'Football',
    how: 'Goals, and who scored each one',
    detail:
      'Every goal is attributed to a player as it is entered, which is what makes a top-scorer board possible at all. In a group a level score is a draw; in a knockout the sport’s own tie-break decides who goes through.',
    units: 'Pitches',
    produces: 'Group tables at three points a win, and a top-scorer board by city and category.',
  },
  {
    sport: 'Badminton',
    how: 'Game by game, deuce handled, ceiling enforced',
    detail:
      'Games are won at eleven or twenty-one depending on the format, and by two after that, up to the ceiling. A game cannot be recorded past the point it would have ended, and the match closes as soon as one side has the games it needs.',
    units: 'Courts',
    produces: 'Games won and lost, straight-game wins, and matches that went the distance.',
  },
  {
    sport: 'Volleyball',
    how: 'Sets, with the deciding set scored short',
    detail:
      'Sets to twenty-five, win by two, with the deciding set played to fifteen. The scorer enters points as they go and the set closes itself when the margin is met.',
    units: 'Courts',
    produces: 'Sets for and against, and group tables where the format calls for them.',
  },
  {
    sport: 'Table tennis',
    how: 'Game by game to eleven, win by two',
    detail:
      'Best of three or best of five. Deuce continues until someone leads by two, and the match ends the moment the required games are won.',
    units: 'Tables',
    produces: 'Games won and lost, and straight-game win records.',
  },
  {
    sport: 'Pickleball',
    how: 'Singles and doubles, serve-side scoring',
    detail:
      'Games to eleven, win by two, singles and doubles draws kept separate. Doubles entries carry both names through the draw so the board and the certificate both read correctly.',
    units: 'Courts',
    produces: 'Games won and lost across singles and doubles draws.',
  },
  {
    sport: 'Carrom',
    how: 'Points to the target board',
    detail:
      'Points accumulate to the target. Boards are recorded individually so a match can be reconstructed rather than just totalled.',
    units: 'Boards',
    produces: 'Points for and against, and boards won.',
  },
  {
    sport: 'Chess',
    how: 'Win, loss, or a draw with the tie-break',
    detail:
      'A result is a win, a loss or a draw. Where a knockout cannot end level, the configured tie-break decides it and is recorded alongside the result.',
    units: 'Boards',
    produces: 'Points, and progression through the rounds.',
  },
];

export const ROLES = [
  {
    who: 'League office',
    scope: 'One account, every city',
    text: 'Creates the season, builds the draw once for a whole sport, moves a match and everything after it moves too.',
    detail: [
      'Set up every tournament with its dates, venue, categories and squad sizes.',
      'Generate the draw for a whole sport at once — groups, knockouts and the byes that come with an uneven entry.',
      'Reschedule a fixture and have the rounds that depend on it follow.',
      'See any fixture, any result and any scorecard, in any city, without switching accounts.',
      'Issue participation and winners’ certificates once a tournament’s results are in.',
    ],
  },
  {
    who: 'City coordinator',
    scope: 'One city',
    text: 'Registers their colleagues, approves the entries, picks the squads and appoints the people who will score.',
    detail: [
      'Register players one at a time or enter a whole team at once.',
      'Check entries before they join the draw — eligibility, category and duplicates.',
      'Choose who plays which sport in which category.',
      'See their city’s full schedule: who plays whom, when, and on which court.',
      'Create a scorer and assign them to a single sport in their own city.',
    ],
  },
  {
    who: 'Scorer',
    scope: 'One sport, one device',
    text: 'Scores at the court on a phone. Each sport knows its own rules, so a game cannot be recorded that cannot happen.',
    detail: [
      'Sees only the sport and city they were given, and only their own matches.',
      'Signed in on one device at a time, so two people cannot score the same match.',
      'Checks players in at the court before the match starts.',
      'Enters the score as the match is played, with an undo for the point that should not have counted.',
      'The score reaches the boards while the match is still going.',
    ],
  },
  {
    who: 'Player',
    scope: 'The mobile app',
    text: 'Sees when and where they play, the code that checks them in, and their own results once the match is done.',
    detail: [
      'Their fixtures: when, where, and against whom.',
      'A code for each match, shown in their own login, scanned at the court.',
      'Their results and the full scorecard for each match.',
      'Their record across the season, sport by sport.',
      'Reminders by WhatsApp and email, and the same notices on the portal.',
    ],
  },
];

export const CAPABILITIES = [
  ['Registration', 'Entries arrive by form or by scanning a code, and land with the right city to be checked.'],
  ['The draw', 'Set the rules once. Groups, knockouts and byes are generated for a whole sport at a time.'],
  ['Scheduling', 'Nobody in two sports is asked to be in two places. Late entries are absorbed without redrawing.'],
  ['Live scoring', 'Ball by ball for cricket, game by game for the racket sports, goals and scorers for football.'],
  ['Boards and screens', 'A court screen set up once on a television keeps itself current for the rest of the day.'],
  ['Standings', 'Group tables at three points a win, plus record boards for runs, wickets, sixes and goals.'],
  ['Certificates', 'Participation and winners’ certificates issued for a tournament once its results are in.'],
  ['Your own website', 'Each association gets a public site on its own address, in its own colours and logo.'],
];

export const TABLE = [
  { pos: 1, team: 'Ridgeway Sports Club', p: 3, w: 2, d: 1, l: 0, gf: 9, ga: 6, gd: '+3', pts: 7 },
  { pos: 2, team: 'Harbour United', p: 3, w: 2, d: 0, l: 1, gf: 5, ga: 3, gd: '+2', pts: 6 },
  { pos: 3, team: 'Fort Athletic', p: 3, w: 1, d: 1, l: 1, gf: 6, ga: 7, gd: '−1', pts: 4 },
  { pos: 4, team: 'Meridian Club', p: 3, w: 0, d: 0, l: 3, gf: 8, ga: 12, gd: '−4', pts: 0 },
];

export const VOICES = [
  { quote: 'We ran four state meets in one season without a single spreadsheet going round on email.', name: 'Rajesh Kumar', role: 'General Secretary, All India Athletics Federation' },
  { quote: 'The scorers picked it up at the court in about ten minutes. That was the part I was worried about.', name: 'Amit Patel', role: 'Technical Director, Indian Badminton Association' },
  { quote: 'Certificates used to take us three weeks after an event. They now go out the same week.', name: 'Dr. Sunita Rao', role: 'Director, State Sports Council, Maharashtra' },
];
